import { useState, useCallback, memo } from "react";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkles, Send, RefreshCw, Save, Edit2, Check, X,
  Play, Zap, GitBranch, Clock, Square, Mail, Database,
  Globe, Brain, AlertCircle, ChevronRight, Wand2
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Node component for preview
const PreviewNode = memo(({ data, type }) => {
  const nodeStyles = {
    start: { bg: 'bg-green-500', icon: Play },
    action: { bg: 'bg-primary', icon: Zap },
    condition: { bg: 'bg-amber-500', icon: GitBranch },
    delay: { bg: 'bg-blue-500', icon: Clock },
    ai: { bg: 'bg-violet-500', icon: Brain },
    email: { bg: 'bg-emerald-500', icon: Mail },
    webhook: { bg: 'bg-orange-500', icon: Globe },
    database: { bg: 'bg-slate-600', icon: Database },
    end: { bg: 'bg-red-500', icon: Square },
  };

  const style = nodeStyles[type] || nodeStyles.action;
  const Icon = style.icon;

  return (
    <div className="px-3 py-2 shadow-md rounded-lg bg-card border min-w-[160px]">
      {type !== 'start' && (
        <Handle
          type="target"
          position={Position.Top}
          className="w-2 h-2 !bg-primary"
        />
      )}
      
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded ${style.bg} flex items-center justify-center text-white`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground capitalize">{type}</p>
          <p className="text-sm font-medium truncate">{data.label}</p>
        </div>
      </div>

      {type !== 'end' && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-2 h-2 !bg-primary"
        />
      )}
    </div>
  );
});

PreviewNode.displayName = 'PreviewNode';

const nodeTypes = {
  start: (props) => <PreviewNode {...props} type="start" />,
  action: (props) => <PreviewNode {...props} type="action" />,
  condition: (props) => <PreviewNode {...props} type="condition" />,
  delay: (props) => <PreviewNode {...props} type="delay" />,
  ai: (props) => <PreviewNode {...props} type="ai" />,
  email: (props) => <PreviewNode {...props} type="email" />,
  webhook: (props) => <PreviewNode {...props} type="webhook" />,
  database: (props) => <PreviewNode {...props} type="database" />,
  end: (props) => <PreviewNode {...props} type="end" />,
};

// Example prompts
const EXAMPLE_PROMPTS = [
  "Email me a summary of new emails every morning at 8am",
  "Post Slack messages to Google Sheets",
  "Alert me on Stripe payments over $1000",
  "Send a Slack notification when I receive an important email",
  "Create a weekly report of all payments and email it to me",
  "Log new Stripe customers to Google Sheets"
];

export default function NLWorkflowGenerator({ isOpen, onClose, onWorkflowCreated }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedWorkflow, setGeneratedWorkflow] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please describe your workflow");
      return;
    }

    setGenerating(true);
    setGeneratedWorkflow(null);

    try {
      const response = await axios.post(
        `${API}/workflows/generate-from-text`,
        { description: prompt },
        { withCredentials: true }
      );

      if (response.data.success) {
        const data = response.data;
        setGeneratedWorkflow(data.workflow);
        setEditedName(data.workflow.name);
        setEditedDescription(data.workflow.description);

        // Set preview nodes/edges
        const previewNodes = data.preview.nodes.map(n => ({
          ...n,
          data: { ...n.data }
        }));
        
        const previewEdges = data.preview.edges.map(e => ({
          ...e,
          animated: true,
          style: { stroke: '#32B8C6' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#32B8C6' }
        }));

        setNodes(previewNodes);
        setEdges(previewEdges);

        if (data.fallback) {
          toast.info("Generated using pattern matching (AI unavailable)");
        } else {
          toast.success(`Workflow generated with ${Math.round(data.workflow.confidence * 100)}% confidence`);
        }
      } else {
        toast.error("Failed to generate workflow");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate workflow");
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveWorkflow = async () => {
    if (!generatedWorkflow) return;

    setSaving(true);
    try {
      // Convert generated workflow to saveable format
      const workflowData = {
        name: editedName || generatedWorkflow.name,
        description: editedDescription || generatedWorkflow.description,
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: n.data
        })),
        edges: edges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target
        })),
        status: "draft",
        generated_from_nl: true,
        original_prompt: prompt,
        trigger_config: generatedWorkflow.trigger,
        action_configs: generatedWorkflow.actions,
        conditions: generatedWorkflow.conditions
      };

      const response = await axios.post(
        `${API}/workflows`,
        workflowData,
        { withCredentials: true }
      );

      toast.success("Workflow saved successfully!");
      
      if (onWorkflowCreated) {
        onWorkflowCreated(response.data);
      }

      // Navigate to workflow builder to edit
      navigate(`/workflows/${response.data.workflow_id}`);
      onClose?.();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save workflow");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPrompt("");
    setGeneratedWorkflow(null);
    setNodes([]);
    setEdges([]);
    setEditMode(false);
  };

  const renderContent = () => (
    <div className="space-y-4">
      {/* Input Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-violet-500" />
          <h3 className="font-semibold">Describe your workflow</h3>
        </div>
        
        <div className="relative">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., Email me a summary of new emails every morning at 8am..."
            className="min-h-[100px] pr-12 resize-none"
            disabled={generating}
          />
          <Button
            size="sm"
            className="absolute bottom-2 right-2"
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
          >
            {generating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Example prompts */}
        <div className="flex flex-wrap gap-2">
          <span className="text-xs text-muted-foreground">Try:</span>
          {EXAMPLE_PROMPTS.slice(0, 3).map((example, i) => (
            <button
              key={i}
              onClick={() => setPrompt(example)}
              className="text-xs px-2 py-1 rounded-full bg-muted hover:bg-muted/80 transition-colors truncate max-w-[200px]"
              disabled={generating}
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      {/* Generated Workflow Preview */}
      {generatedWorkflow && (
        <div className="space-y-4 pt-4 border-t">
          {/* Workflow Info */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {editMode ? (
                <div className="space-y-2">
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    placeholder="Workflow name"
                    className="font-semibold"
                  />
                  <Textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    placeholder="Description"
                    className="text-sm resize-none"
                    rows={2}
                  />
                </div>
              ) : (
                <>
                  <h4 className="font-semibold text-lg">{editedName || generatedWorkflow.name}</h4>
                  <p className="text-sm text-muted-foreground">{editedDescription || generatedWorkflow.description}</p>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge 
                variant={generatedWorkflow.confidence >= 0.8 ? "default" : "secondary"}
                className="text-xs"
              >
                {Math.round(generatedWorkflow.confidence * 100)}% confidence
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? <Check className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Explanation */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-sm">{generatedWorkflow.explanation}</p>
            </div>
          </div>

          {/* Workflow Preview */}
          <div className="h-[300px] border rounded-lg overflow-hidden bg-background">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              fitView
              className="dot-pattern"
              defaultEdgeOptions={{
                animated: true,
                style: { stroke: '#32B8C6' },
                markerEnd: { type: MarkerType.ArrowClosed, color: '#32B8C6' }
              }}
            >
              <Controls className="!bg-card/90" />
              <Background variant="dots" gap={16} size={1} color="rgba(50, 184, 198, 0.1)" />
            </ReactFlow>
          </div>

          {/* Trigger & Actions Summary */}
          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-green-500/5 border-green-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Play className="w-4 h-4 text-green-500" />
                  Trigger
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium capitalize">
                  {generatedWorkflow.trigger.type}
                  {generatedWorkflow.trigger.app && ` (${generatedWorkflow.trigger.app})`}
                </p>
                {generatedWorkflow.trigger.time && (
                  <p className="text-xs text-muted-foreground">
                    At {generatedWorkflow.trigger.time}
                  </p>
                )}
                {generatedWorkflow.trigger.frequency && (
                  <p className="text-xs text-muted-foreground">
                    {generatedWorkflow.trigger.frequency}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-primary/5 border-primary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  Actions ({generatedWorkflow.actions?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {generatedWorkflow.actions?.slice(0, 3).map((action, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs">
                      <ChevronRight className="w-3 h-3 text-muted-foreground" />
                      <span className="font-medium">{action.app}</span>
                      <span className="text-muted-foreground">→ {action.action}</span>
                    </div>
                  ))}
                  {(generatedWorkflow.actions?.length || 0) > 3 && (
                    <p className="text-xs text-muted-foreground">
                      +{generatedWorkflow.actions.length - 3} more
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Conditions */}
          {generatedWorkflow.conditions?.length > 0 && (
            <Card className="bg-amber-500/5 border-amber-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-amber-500" />
                  Conditions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedWorkflow.conditions.map((cond, i) => (
                  <div key={i} className="text-sm">
                    If <code className="bg-muted px-1 rounded">{cond.field}</code>{' '}
                    {cond.operator} {cond.value}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );

  // If used as a standalone component (not in dialog)
  if (!isOpen && isOpen !== false) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-500" />
            Natural Language Workflow Generator
          </CardTitle>
          <CardDescription>
            Describe your automation in plain English and let AI build it for you
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderContent()}
          {generatedWorkflow && (
            <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" onClick={handleReset}>
                <X className="w-4 h-4 mr-2" /> Start Over
              </Button>
              <Button onClick={handleSaveWorkflow} disabled={saving}>
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Workflow
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Dialog version
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-500" />
            Natural Language Workflow Generator
          </DialogTitle>
          <DialogDescription>
            Describe your automation in plain English and let AI build it for you
          </DialogDescription>
        </DialogHeader>
        
        {renderContent()}
        
        <DialogFooter className="mt-4">
          {generatedWorkflow ? (
            <>
              <Button variant="outline" onClick={handleReset}>
                <X className="w-4 h-4 mr-2" /> Start Over
              </Button>
              <Button onClick={handleSaveWorkflow} disabled={saving}>
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Workflow
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
